import type {
  AdminConversation,
  AdminMessage,
  AdminQualityCheck,
  AllowlistAction,
  AllowlistAuditEntry,
  AllowlistEnvironment,
  BotIpNameListResponse,
  BotWechatAccountIpName,
  CampaignCreateInput,
  CampaignDetailResponse,
  CampaignListResponse,
  CampaignMaterial,
  CampaignMaterialCreateInput,
  CampaignMetricsResponse,
  CampaignDirectRunCreateInput,
  CampaignPreviewCreateInput,
  CampaignTextRepairPreviewCreateInput,
  CampaignPreviewCandidateCountResponse,
  CampaignPreviewResponse,
  CampaignRunCreateInput,
  CampaignRunResponse,
  CampaignUpdateInput,
  ConversationFeedback,
  ConversationMode,
  EffectiveAllowlistResponse,
  EnvironmentRoutesResponse,
  ListConversationsOptions,
  ListConversationsResponse,
  MessageFeedback,
  MessageFeedbackInput,
  ProfileRefineryMode,
  ProfileMemoryWarningsResolveResponse,
  ProfileRefineryRunResponse,
  GroupAnalysisEvidenceResponse,
  GroupAnalysisOverviewResponse,
  GroupAnalysisTodayResponse,
  GroupComplianceReview,
  GroupComplianceReviewStatus,
  GroupAnalysisResponse,
  GroupAnalysisSort,
  GroupAnalysisView,
  GroupDashboardEvidenceRule,
  GroupDashboardItemsResponse,
  GroupDashboardMetric,
  GroupDashboardTotals,
  GroupDashboardDailyPoint,
  GroupStaffRankingResponse,
  GroupConversation,
  DesignerGroupMapping,
  GroupMessage,
  GroupStats,
} from './types.js';
import { adminBasePath } from './base.js';

let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

export class ApiClientError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const { headers, ...rest } = options ?? {};
  const requestHeaders = new Headers(headers);
  if (!requestHeaders.has('Accept')) {
    requestHeaders.set('Accept', 'application/json');
  }
  const response = await fetch(`${adminBasePath()}${path}`, {
    cache: 'no-store',
    ...rest,
    headers: requestHeaders,
  });
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    if (response.status === 401) {
      unauthorizedHandler?.();
    }
    const message = typeof data.error === 'string' ? data.error : `HTTP ${response.status}`;
    throw new ApiClientError(message, response.status);
  }
  return data as T;
}

export function listConversations(
  options: ListConversationsOptions = {},
  init?: RequestInit,
): Promise<ListConversationsResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(options.limit ?? 100));
  params.set('whitelist', options.whitelist !== false ? '1' : '0');
  if (options.mode) params.set('mode', options.mode);
  if (options.q) {
    params.set('q', options.q);
  } else if (options.days !== undefined) {
    params.set('days', String(options.days));
  }
  if (options.offset !== undefined) params.set('offset', String(options.offset));
  return request<ListConversationsResponse>(`/api/conversations?${params.toString()}`, init);
}

export function listMessages(
  friendId: number,
  limit?: number,
  offset?: number,
): Promise<{ messages: AdminMessage[]; aiCost?: { totalCny: number; turns: number }; hasMore?: boolean }> {
  const params = new URLSearchParams();
  if (limit !== undefined) params.set('limit', String(limit));
  if (offset !== undefined) params.set('offset', String(offset));
  const query = params.toString();
  return request<{ messages: AdminMessage[]; aiCost?: { totalCny: number; turns: number }; hasMore?: boolean }>(
    `/api/conversations/${friendId}/messages${query ? `?${query}` : ''}`,
  );
}

export function listGroupConversations(options: {
  q?: string;
  days?: number;
  limit?: number;
  offset?: number;
  favoriteOnly?: boolean;
  mode?: ConversationMode;
} = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(options.limit ?? 50));
  params.set('offset', String(options.offset ?? 0));
  if (options.q) params.set('q', options.q);
  else if (options.days !== undefined) params.set('days', String(options.days));
  if (options.favoriteOnly) params.set('favoriteOnly', 'true');
  if (options.mode) params.set('mode', options.mode);
  return request<{ conversations: GroupConversation[]; pagination: ListConversationsResponse['pagination'] }>(
    `/api/group-conversations?${params.toString()}`,
  );
}

export function listGroupMessages(
  id: number,
  options: { limit?: number; offset?: number; startDate?: string; endDate?: string } = {},
) {
  const params = new URLSearchParams();
  params.set('limit', String(options.limit ?? 100));
  params.set('offset', String(options.offset ?? 0));
  if (options.startDate) params.set('startDate', options.startDate);
  if (options.endDate) params.set('endDate', options.endDate);
  return request<{
    conversation: GroupConversation;
    messages: GroupMessage[];
    pagination: ListConversationsResponse['pagination'];
  }>(
    `/api/group-conversations/${id}/messages?${params.toString()}`,
  );
}

export function setGroupConversationMode(id: number, mode: ConversationMode) {
  return request<{ conversation: GroupConversation }>(`/api/group-conversations/${id}/mode`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode }),
  });
}

export function setGroupConversationFavorite(id: number, favorite: boolean) {
  return request<{ conversation: GroupConversation }>(`/api/group-conversations/${id}/favorite`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ favorite }),
  });
}

export function getDesignerGroupMapping(id: number) {
  return request<{ mapping: DesignerGroupMapping | null }>(
    `/api/group-conversations/${id}/designer-mapping`,
  );
}

export function saveDesignerGroupMapping(id: number, input: {
  targetClusterId: number;
  targetWxClusterId: string;
  targetClusterName?: string | null;
  enabled: boolean;
}) {
  return request<{ mapping: DesignerGroupMapping }>(
    `/api/group-conversations/${id}/designer-mapping`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
}

export function getGroupAnalysis(id: number) {
  return request<GroupAnalysisResponse>(`/api/group-conversations/${id}/analysis`);
}

export function listGroupAnalyses(options: {
  q?: string;
  stage?: string;
  intent?: string;
  tier?: string;
  sort?: GroupAnalysisSort;
  includeUnanalyzed?: boolean;
  /** 名单视图；缺省/all 为现状行为。 */
  view?: GroupAnalysisView;
  /** 客群阶段筛选（computed.lifecycleStage）。 */
  lifecycle?: 'repeat' | 'customer' | 'intent' | 'new';
  /** 只看当前快照有成交信号的群。 */
  dealSignals?: boolean;
  /** 只看沉睡群（最后客户消息 ≥ 30 天），可叠加任意视图。 */
  dormant?: boolean;
  /** 视图与计数的周期（7|30|90），缺省 30。 */
  days?: number;
  limit?: number;
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(options.limit ?? 50));
  params.set('offset', String(options.offset ?? 0));
  if (options.view && options.view !== 'all') params.set('view', options.view);
  if (options.lifecycle) params.set('lifecycle', options.lifecycle);
  if (options.dealSignals) params.set('dealSignals', 'true');
  if (options.dormant) params.set('dormant', 'true');
  if (options.days) params.set('days', String(options.days));
  if (options.q) params.set('q', options.q);
  if (options.stage) params.set('stage', options.stage);
  if (options.intent) params.set('intent', options.intent);
  if (options.tier) params.set('tier', options.tier);
  if (options.sort) params.set('sort', options.sort);
  if (options.includeUnanalyzed) params.set('includeUnanalyzed', 'true');
  return request<GroupAnalysisOverviewResponse>(`/api/group-analyses?${params.toString()}`);
}

export function listGroupAnalysesToday(options: { days?: number; q?: string; limit?: number; offset?: number } = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(options.limit ?? 50));
  params.set('offset', String(options.offset ?? 0));
  if (options.days) params.set('days', String(options.days));
  if (options.q) params.set('q', options.q);
  return request<GroupAnalysisTodayResponse>(`/api/group-analyses/today?${params.toString()}`);
}

export function reviewGroupComplianceFlag(conversationId: number, flagKey: string, status: GroupComplianceReviewStatus) {
  return request<GroupComplianceReview>(`/api/group-analyses/${conversationId}/compliance/${encodeURIComponent(flagKey)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
}

export function getGroupStaffRanking(days: number) {
  return request<GroupStaffRankingResponse>(`/api/group-dashboard/staff?days=${days}`);
}

export function getGroupDashboardTrend(limit = 90) {
  return request<{ points: GroupDashboardDailyPoint[] }>(`/api/group-dashboard/trend?limit=${limit}`);
}

export function getGroupDashboard(days: number) {
  return request<GroupDashboardTotals>(`/api/group-dashboard?days=${days}`);
}

export function listGroupDashboardItems(metric: GroupDashboardMetric, options: { days: number; limit?: number; offset?: number }) {
  const params = new URLSearchParams();
  params.set('days', String(options.days));
  params.set('limit', String(options.limit ?? 50));
  params.set('offset', String(options.offset ?? 0));
  return request<GroupDashboardItemsResponse>(`/api/group-dashboard/${metric}/items?${params.toString()}`);
}

export function getGroupAnalysisEvidence(conversationId: number, messageId: number, rule: GroupDashboardEvidenceRule) {
  return request<GroupAnalysisEvidenceResponse>(
    `/api/group-conversations/${conversationId}/analysis-evidence?messageId=${messageId}&rule=${rule}`,
  );
}

export function getGroupMessageContext(conversationId: number, messageId: number, radius = 3) {
  return request<{ messages: GroupMessage[] }>(
    `/api/group-conversations/${conversationId}/messages/${messageId}/context?radius=${radius}`,
  );
}

export function runGroupAnalysis(conversationId: number) {
  return request<{ status: string; runId?: number }>(`/api/group-conversations/${conversationId}/analysis/run`, {
    method: 'POST',
  });
}

export function getGroupStats(days?: number) {
  const query = days === undefined ? '' : `?days=${days}`;
  return request<{ stats: GroupStats }>(`/api/group-conversations/stats${query}`);
}

export function sendReply(friendId: number, text: string): Promise<{ message: AdminMessage }> {
  return request<{ message: AdminMessage }>(`/api/conversations/${friendId}/reply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
}

export function setConversationMode(friendId: number, mode: ConversationMode): Promise<{ conversation: AdminConversation }> {
  return request<{ conversation: AdminConversation }>(`/api/conversations/${friendId}/mode`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode }),
  });
}

export function runQualityCheck(friendId: number): Promise<{ qualityCheck: AdminQualityCheck }> {
  return request<{ qualityCheck: AdminQualityCheck }>(`/api/conversations/${friendId}/quality-check`, {
    method: 'POST',
  });
}

export function runProfileRefinery(
  friendId: number,
  input: { mode: ProfileRefineryMode; limit?: number },
): Promise<ProfileRefineryRunResponse> {
  return request<ProfileRefineryRunResponse>(`/api/conversations/${friendId}/profile-refinery`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function resolveProfileMemoryWarnings(friendId: number): Promise<ProfileMemoryWarningsResolveResponse> {
  return request<ProfileMemoryWarningsResolveResponse>(`/api/conversations/${friendId}/profile-memory/warnings/resolve`, {
    method: 'POST',
  });
}

export function listQualityChecks(): Promise<{ qualityChecks: AdminQualityCheck[] }> {
  return request<{ qualityChecks: AdminQualityCheck[] }>('/api/quality-checks');
}

export function getMessageFeedback(messageId: number): Promise<{ feedback: MessageFeedback | null }> {
  return request<{ feedback: MessageFeedback | null }>(`/api/messages/${messageId}/feedback`);
}

export function saveMessageFeedback(
  messageId: number,
  input: MessageFeedbackInput,
): Promise<{ feedback: MessageFeedback }> {
  return request<{ feedback: MessageFeedback }>(`/api/messages/${messageId}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function getConversationFeedback(
  conversationId: number,
): Promise<{ feedback: ConversationFeedback | null }> {
  return request<{ feedback: ConversationFeedback | null }>(`/api/conversations/${conversationId}/feedback`);
}

export function saveConversationFeedback(
  conversationId: number,
  summary: string,
): Promise<{ feedback: ConversationFeedback }> {
  return request<{ feedback: ConversationFeedback }>(`/api/conversations/${conversationId}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ summary }),
  });
}

export function getEffectiveAllowlist(): Promise<EffectiveAllowlistResponse> {
  return request<EffectiveAllowlistResponse>('/api/allowlist/effective');
}

export function listBotIpNames(): Promise<BotIpNameListResponse> {
  return request<BotIpNameListResponse>('/api/bot-ip-names');
}

export function updateBotIpName(
  wechatRecordId: number,
  ipName: string,
): Promise<{ account: BotWechatAccountIpName }> {
  return request<{ account: BotWechatAccountIpName }>(`/api/bot-ip-names/${wechatRecordId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ipName }),
  });
}

export function getEnvironmentRoutes(): Promise<EnvironmentRoutesResponse> {
  return request<EnvironmentRoutesResponse>('/api/environment/routes');
}

export function updateFriendAllowlist(
  friendId: number,
  input: {
    action: AllowlistAction;
    environment: AllowlistEnvironment;
    reason?: string;
    operator?: string;
  },
): Promise<{ decision: EffectiveAllowlistResponse['conversations'][number]['allowlist']; audit: AllowlistAuditEntry }> {
  return request<{ decision: EffectiveAllowlistResponse['conversations'][number]['allowlist']; audit: AllowlistAuditEntry }>(
    `/api/allowlist/friends/${friendId}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
}

export function listFriendAllowlistAudit(friendId: number): Promise<{ audit: AllowlistAuditEntry[] }> {
  return request<{ audit: AllowlistAuditEntry[] }>(`/api/allowlist/friends/${friendId}/audit`);
}

export function listCampaigns(): Promise<CampaignListResponse> {
  return request<CampaignListResponse>('/api/campaigns');
}

export function getCampaign(campaignId: number): Promise<CampaignDetailResponse> {
  return request<CampaignDetailResponse>(`/api/campaigns/${campaignId}`);
}

export function createCampaign(input: CampaignCreateInput): Promise<CampaignDetailResponse> {
  return request<CampaignDetailResponse>('/api/campaigns', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function updateCampaign(
  campaignId: number,
  input: CampaignUpdateInput,
): Promise<CampaignDetailResponse> {
  return request<CampaignDetailResponse>(`/api/campaigns/${campaignId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function addCampaignMaterial(
  campaignId: number,
  input: CampaignMaterialCreateInput,
): Promise<{ material: CampaignMaterial; warnings?: string[] }> {
  return request<{ material: CampaignMaterial; warnings?: string[] }>(`/api/campaigns/${campaignId}/materials`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function deleteCampaignMaterial(
  campaignId: number,
  materialId: number,
): Promise<void> {
  return request<void>(`/api/campaigns/${campaignId}/materials/${materialId}`, {
    method: 'DELETE',
  });
}

export function extractCampaign(
  campaignId: number,
  input: { message?: string } = {},
): Promise<CampaignDetailResponse> {
  return request<CampaignDetailResponse>(`/api/campaigns/${campaignId}/extract`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function createCampaignPreview(
  campaignId: number,
  input: CampaignPreviewCreateInput,
): Promise<CampaignPreviewResponse> {
  return request<CampaignPreviewResponse>(`/api/campaigns/${campaignId}/previews`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function createCampaignTextRepairPreview(
  campaignId: number,
  input: CampaignTextRepairPreviewCreateInput,
): Promise<CampaignPreviewResponse> {
  return request<CampaignPreviewResponse>(`/api/campaigns/${campaignId}/text-repair-preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function getCampaignPreview(
  campaignId: number,
  previewId: number,
  options: { limit?: number; offset?: number } = {},
): Promise<CampaignPreviewResponse> {
  const params = new URLSearchParams();
  if (options.limit !== undefined) params.set('limit', String(options.limit));
  if (options.offset !== undefined) params.set('offset', String(options.offset));
  const query = params.toString();
  return request<CampaignPreviewResponse>(`/api/campaigns/${campaignId}/previews/${previewId}${query ? `?${query}` : ''}`);
}

export function getCampaignPreviewCandidateCount(campaignId: number): Promise<CampaignPreviewCandidateCountResponse> {
  return request<CampaignPreviewCandidateCountResponse>(`/api/campaigns/${campaignId}/preview-candidates/count`);
}

export function pauseCampaignPreview(campaignId: number, previewId: number): Promise<CampaignPreviewResponse> {
  return request<CampaignPreviewResponse>(`/api/campaigns/${campaignId}/previews/${previewId}/pause`, {
    method: 'POST',
  });
}

export function resumeCampaignPreview(campaignId: number, previewId: number): Promise<CampaignPreviewResponse> {
  return request<CampaignPreviewResponse>(`/api/campaigns/${campaignId}/previews/${previewId}/resume`, {
    method: 'POST',
  });
}

export function cancelCampaignPreview(campaignId: number, previewId: number): Promise<CampaignPreviewResponse> {
  return request<CampaignPreviewResponse>(`/api/campaigns/${campaignId}/previews/${previewId}/cancel`, {
    method: 'POST',
  });
}

export function createCampaignRun(
  campaignId: number,
  input: CampaignRunCreateInput,
): Promise<CampaignRunResponse> {
  return request<CampaignRunResponse>(`/api/campaigns/${campaignId}/runs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function createCampaignDirectRun(
  campaignId: number,
  input: CampaignDirectRunCreateInput,
): Promise<CampaignPreviewResponse & CampaignRunResponse> {
  return request<CampaignPreviewResponse & CampaignRunResponse>(`/api/campaigns/${campaignId}/direct-run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function getCampaignRun(
  campaignId: number,
  runId: number,
  options: { limit?: number; offset?: number } = {},
): Promise<CampaignRunResponse> {
  const params = new URLSearchParams();
  if (options.limit !== undefined) params.set('limit', String(options.limit));
  if (options.offset !== undefined) params.set('offset', String(options.offset));
  const query = params.toString();
  return request<CampaignRunResponse>(`/api/campaigns/${campaignId}/runs/${runId}${query ? `?${query}` : ''}`);
}

export function getCampaignMetrics(campaignId: number): Promise<CampaignMetricsResponse> {
  return request<CampaignMetricsResponse>(`/api/campaigns/${campaignId}/metrics`);
}
